import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Breadcrumbs from 'components/Breadcrumbs'
import DateFilter from 'components/DateFilter'
import ExpenseList from 'components/ExpenseList'
import LoadingIcon from 'components/LoadingIcon'
import OrderFilter from 'components/OrderFilter'
import Pagination from 'components/Pagination'
import { deleteExpense, fetchExpenses } from 'config/api'
import routes from 'config/routes'
import { useBankrolls } from 'hooks'
import React from 'react'
import { Button, Card, Col, Row } from 'react-bootstrap'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { Link, Route, Switch, useLocation } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import { Expense } from 'screens'
import { sleep } from 'utils'
import BankrollFilter from 'components/BankrollFilter'
import PlayerFilter from 'components/PlayerFilter'

const DEFAULT_PAGE = 1
const DEFAULT_PER_PAGE = 10

function Expenses() {
  const {
    currentBankroll,
    bankrolls,
    selectBankroll,
    createBankroll,
    players,
  } = useBankrolls()
  const { addToast } = useToasts()
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  const page = parseInt(params.get('page')) || DEFAULT_PAGE
  const perPage = parseInt(params.get('per_page')) || DEFAULT_PER_PAGE
  const [orderFilter, setOrderFilter] = React.useState(null)
  const [playerFilter, setPlayerFilter] = React.useState(null)
  const [dateFilters, setDateFilters] = React.useState({
    date__gte: null,
    date__lte: null,
  })
  const qClient = useQueryClient()

  const qExpenses = useQuery(
    ['expenses', { page, orderFilter, playerFilter, dateFilters }],
    async ({ queryKey }) => {
      const [, { page, orderFilter, playerFilter, dateFilters }] = queryKey
      // Minimum load time is .5 seconds so the loading icon doesn't
      // flash too quickly.
      await sleep(500)
      return fetchExpenses(currentBankroll.id, {
        ordering: orderFilter,
        player: playerFilter,
        page,
        ...dateFilters,
      })
    },
    {
      onError: err => {
        addToast('Error retrieving expenses', {
          appearance: 'error',
          autoDismiss: true,
        })
        console.error('Error retrieving expenses')
        console.error(err)
      },
    },
  )

  const mExpenses = useMutation(
    id => {
      return deleteExpense(currentBankroll.id, id)
    },
    {
      onSuccess: () => {
        qClient.invalidateQueries('bankrolls')
        qClient.invalidateQueries('expenses')
      },
    },
  )

  const _deleteExpense = async id => {
    try {
      await mExpenses.mutateAsync(id)
      addToast('Expense deleted', { autoDismiss: true, appearance: 'success' })
    } catch (err) {
      addToast(err.message, { autoDismiss: true, appearance: 'error' })
      console.error('Error deleting expense')
      console.error(err)
    }
  }

  const handleDateFilter = React.useCallback((start, end) => {
    setDateFilters({
      date__gte: start,
      date__lte: end,
    })
  }, [])

  const handlePlayerFilter = React.useCallback(setPlayerFilter, [])

  const handleOrderFilter = order => {
    // When ordering we always want to sort by the most recent date
    // after the initial field is specified.
    setOrderFilter(order && `${order},-date`)
  }

  return (
    <Switch>
      <Route exact path={routes.expenseNew} component={Expense} />
      <Route exact path={routes.expenseEdit} component={Expense} />
      <Route>
        <div id="expenses">
          <Breadcrumbs
            crumbs={[
              ['DASHBOARD', routes.dashboard],
              ['EXPENSES', routes.expenses],
            ]}
          />
          <Row>
            <Col className="d-flex flex-wrap justify-content-start justify-content-md-between">
              <BankrollFilter
                current={currentBankroll}
                choices={bankrolls}
                onChange={selectBankroll}
                onCreate={createBankroll}
              />
              <div>
                <DateFilter
                  disabled={qExpenses.isLoading}
                  onChange={handleDateFilter}
                />
                {currentBankroll.shared_users.length > 0 && (
                  <PlayerFilter
                    disabled={qExpenses.isLoading}
                    onChange={handlePlayerFilter}
                    choices={players}
                  />
                )}
                <OrderFilter
                  disabled={qExpenses.isLoading}
                  onChange={handleOrderFilter}
                  choices={[
                    ['Most Recent', null],
                    ['Earnings (Hi-Lo)', '-earnings'],
                    ['Earnings (Lo-Hi)', 'earnings'],
                    ['Location (A-Z)', 'location'],
                    ['Location (Z-A)', '-location'],
                  ]}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col className="mt-3">
              <Card className="shadow">
                <Card.Body>
                  <div className="mb-2 clearfix">
                    {qExpenses.isSuccess &&
                    qExpenses.data.results.length > 0 ? (
                      <Button
                        variant="warning"
                        size="sm"
                        as={Link}
                        to={routes.expenseNew}
                        className="mb-2 float-right"
                      >
                        <FontAwesomeIcon icon={['far', 'plus']} fixedWidth />
                        ADD EXPENSE
                      </Button>
                    ) : null}
                    <Card.Title>EXPENSES</Card.Title>
                  </div>
                  {qExpenses.isLoading ? (
                    <LoadingIcon />
                  ) : qExpenses.isError ? (
                    <p className="my-5 text-center font-weight-bold">
                      Unable to retreive expenses
                    </p>
                  ) : qExpenses.data.results.length > 0 ? (
                    <>
                      <ExpenseList
                        expenses={qExpenses.data.results}
                        onDelete={_deleteExpense}
                      />
                      <Pagination
                        count={qExpenses.data.count}
                        page={page}
                        perPage={perPage}
                      />
                    </>
                  ) : dateFilters.date__gte !== null ||
                    dateFilters.date__lte !== null ||
                    orderFilter !== null ||
                    playerFilter !== null ? (
                    <div className="my-5 text-center">NO EXPENSES FOUND</div>
                  ) : (
                    <div className="my-5 text-center">
                      <Button
                        variant="warning"
                        as={Link}
                        to={routes.expenseNew}
                      >
                        <FontAwesomeIcon icon={['far', 'plus']} fixedWidth />
                        ADD EXPENSE
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      </Route>
    </Switch>
  )
}

export default Expenses
